import _ from 'lodash';
import {action, computed, observable} from 'mobx';
import {DomainObject, oneOf} from 'shared/store';
import {t} from 'shared/core';
import AttachedDocument from 'shared/stores/documents/AttachedDocument';
import NotificationRecipient from './NotificationRecipient';
import AssignmentRule from './AssignmentRule';
import DocumentAction from './DocumentAction';
import {calendarDate} from 'shared/tools';

const COLOURS = [
  '#1776F5',
  '#F64A6C',
  '#7E66E7',
  '#CB7525',
  '#80475E'
];

class CompanyDocument extends DomainObject {
  @observable id;
  @observable name = t('models.company_document.DEFAULT_NAME');
  @observable signatureRequired;
  @observable previewFile;
  @observable documentType;
  @observable locale;
  @observable order;
  @observable folderId;
  @observable notificationRecipients = [];
  @observable assignmentRules = [];
  @observable documentActions = [];
  @observable createdAt;
  @observable completedAt;

  // PDF Template
  @observable annotations = [];
  @observable originalFile;

  // Rich Text Template
  @observable richText = `<br/>`;
  @observable lexicalState;

  @observable actionedEmployeeIds = [];

  @action merge(other) {
    super.merge(other, {
      previewFile: AttachedDocument,
      originalFile: AttachedDocument,
      documentActions: [DocumentAction],
      notificationRecipients: [NotificationRecipient],
      assignmentRules: [AssignmentRule],
      documentType: oneOf(['rich_text_template', 'pdf_template', 'static_document'])
    });
  }

  @action addRecipient(recipient) {
    this.notificationRecipients.push(recipient);
  }

  @action removeRecipient(recipient) {
    _.remove(this.notificationRecipients, {value: recipient.value});
  }

  @action addRule(assignee) {
    this.assignmentRules.push(new AssignmentRule({
      employee: assignee.employee,
      assigneeType: assignee.assigneeType,
      companyDocument: this,
      color: this.selectableColors[0],
      order: this.assignmentRules.length,
      orderOffset: this.hasExplicitEmployeeRule ? 1 : 2
    }));
  }

  @action removeRule(assigneeId) {
    _.remove(this.assignmentRules, {assignee: assigneeId});
    this.sortedAssignmentRules.forEach((rule, index) => rule.order = index);
  }

  @computed get createdAtView() {
    return calendarDate(this.createdAt);
  }

  @computed get completedAtView() {
    return calendarDate(this.completedAt);
  }

  @computed get primaryDocumentAction() {
    return this.sortedDocumentActions[0];
  }

  @computed get typeView() {
    switch (this.documentType) {
      case 'rich_text_template':
      case 'pdf_template':
      case 'static_document':
        return t(`models.company_document.document_type.${this.documentType}`);
      default:
        throw new Error(`Document type of ${this.documentType} is not supported.`);
    }
  }

  @computed get hasFiles() {
    return !_.isEmpty(this.previewFile);
  }

  @computed get sortedAssignmentRules() {
    return _.sortBy(this.assignmentRules, 'order');
  }

  @computed get hasExplicitEmployeeRule() {
    return _.some(this.assignmentRules, {assigneeType: 'employee'});
  }

  @computed get sortedDocumentActions() {
    return _.sortBy(this.documentActions, 'order');
  }

  @computed get selectableColors() {
    const usedColorCounts = _.countBy(this.assignmentRules, 'color');
    const colorCounts = _.fromPairs(
      COLOURS.map(color => [color, usedColorCounts[color] || 0])
    );
    const minUsageCount = _.min(_.values(colorCounts));

    return COLOURS.filter(color => colorCounts[color] === minUsageCount);
  }

  @computed get assignedToPrimaryEmployee() {
    return _.isEmpty(this.actionedEmployeeIds);
  }
}

export default CompanyDocument;
